import { supabase } from '@/lib/supabase';
import { endLocalSession } from '@/lib/endLocalSession';
import { buildApiUrl } from '@/utils/apiUrls';
import { withShareCredential } from '@/utils/shareCredential';

// Returns the current Supabase session access token, or null when signed out.
export const getAccessToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
};

// A 401 from our API while this browser still holds a session token means the
// server no longer accepts it. The usual cause is the session being revoked
// elsewhere (a sign-out on another device, an admin revocation): the local
// access token is untouched and stays "valid" until it expires, so the app
// keeps rendering as signed in while every request fails. Ask Supabase whether
// the session still exists; when it says no, drop the local copy so the auth
// listener sends the user to the login page instead of a page full of errors.
// One check per token — a burst of parallel 401s must not fan out into a
// burst of getUser calls and sign-outs.
let staleTokenCheck = null;
let lastStaleToken = null;

const isSessionGone = (error) => {
  const code = error?.code || error?.status;
  const message = String(error?.message || '');
  return (
    code === 'session_not_found' ||
    code === 403 ||
    code === 401 ||
    /session.*(not found|missing)/i.test(message)
  );
};

const dropSessionIfRevoked = async (token) => {
  if (lastStaleToken === token && staleTokenCheck) return staleTokenCheck;
  lastStaleToken = token;
  staleTokenCheck = (async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (data?.user && !error) return false;
      if (!isSessionGone(error)) return false;
      // A logout request that fails on the way out would leave the dead
      // session in place; drop it here regardless.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
      if (signOutError) await endLocalSession();
      return true;
    } catch {
      return false;
    } finally {
      staleTokenCheck = null;
    }
  })();
  return staleTokenCheck;
};

// fetch() wrapper for the MealOps API that attaches the signed-in user's
// Supabase session token as a Bearer credential. The server's order/payment
// routes require this; a call made while signed out simply omits the header and
// the server responds 401. `path` is a relative API path (e.g. "/api/orders/…")
// or an absolute URL — both are handled by buildApiUrl.
export const authorizedFetch = async (path, options = {}) => {
  const token = await getAccessToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Signed out, the caller may still be a legitimate share-link visitor. The
  // API's per-restaurant reads require such a caller to prove it, so pass the
  // credential this page is holding. Signed-in callers never send one — their
  // bearer token is the stronger claim, and the server prefers it.
  const target = token ? path : withShareCredential(path);
  const response = await fetch(buildApiUrl(target), { ...options, headers });
  if (response.status === 401 && token) {
    // Not awaited: the caller gets its 401 as before; the redirect follows.
    void dropSessionIfRevoked(token);
  }
  return response;
};

export default authorizedFetch;
